import { Link } from '@tanstack/react-router'
import { motion } from 'framer-motion'
import { Sparkles, ArrowRight, Layers } from 'lucide-react'
import { useProjects, useCategory } from '@/hooks/useProjects'
import { useTranslation } from '@/hooks/useTypedTranslation'
import { useLocalizedPath } from '@/hooks/useLocalizedPath'
import { ProjectCard } from '@/components/ProjectCard'
import { ImageWithFallback } from '@/components/figma/ImageWithFallback'
import { PageLayout } from '@/components/PageLayout'
import { StructuredData, generateBreadcrumbs } from '@/components/StructuredData'

type HundredSeriesId = 'launch100' | 'spy100' | 'ship100' | 'grow100'
type NarrativeKey = 'intro' | 'spy' | 'ship' | 'grow'

const SERIES_NARRATIVE: Record<HundredSeriesId, NarrativeKey> = {
  launch100: 'intro',
  spy100: 'spy',
  ship100: 'ship',
  grow100: 'grow',
}

const STAGE_KEYS = ['spy', 'launch', 'ship', 'grow'] as const
type StageKey = (typeof STAGE_KEYS)[number]

const seriesColors: Record<HundredSeriesId, { gradient: string; accent: string; glow: string }> = {
  spy100: { 
    gradient: 'from-orange-500/20 via-amber-500/10 to-transparent',
    accent: 'text-orange-400',
    glow: 'bg-orange-500/10'
  },
  launch100: { 
    gradient: 'from-blue-500/20 via-cyan-500/10 to-transparent',
    accent: 'text-blue-400',
    glow: 'bg-blue-500/10'
  },
  ship100: { 
    gradient: 'from-emerald-500/20 via-green-500/10 to-transparent',
    accent: 'text-emerald-400',
    glow: 'bg-emerald-500/10'
  },
  grow100: { 
    gradient: 'from-purple-500/20 via-violet-500/10 to-transparent',
    accent: 'text-purple-400',
    glow: 'bg-purple-500/10'
  },
}

interface HundredSeriesPageProps {
  seriesId: HundredSeriesId
}

export default function HundredSeriesPage({ seriesId }: HundredSeriesPageProps) {
  const { data: projects, isLoading } = useProjects(seriesId)
  const { data: category } = useCategory(seriesId)
  const { t, i18n } = useTranslation()
  const { getLocalizedPath } = useLocalizedPath()

  

  const narrativeKey = SERIES_NARRATIVE[seriesId]
  const narrative = t.collections.challenge100.narrative[narrativeKey]
  const stages = t.collections.challenge100.stages
  const colors = seriesColors[seriesId]

  const allProjects = projects || []
  const featured = allProjects.find((p) => p.is_featured) || allProjects[0] || null

  const lang = i18n.language as 'en' | 'zh';
  const seriesTitle = category?.title || seriesId;
  const breadcrumbs = generateBreadcrumbs([
    { label: 'Challenge 100', path: '/challenge100' },
    { label: seriesTitle, path: `/${seriesId}` }
  ], lang);

  return (
    <PageLayout>
      <StructuredData type="website" breadcrumbs={breadcrumbs} />
      {/* Hero Section - 全屏暗色背景 */}
      <div className="relative min-h-[60vh] bg-black overflow-hidden">
        {/* 背景渐变效果 */}
        <div className={`absolute inset-0 bg-gradient-to-br ${colors.gradient}`} />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-accent-purple/5 via-transparent to-transparent" />
        
        {/* 装饰性光效 */}
        <div className={`absolute top-1/3 left-1/4 w-96 h-96 ${colors.glow} rounded-full blur-3xl animate-pulse`} />
        <div className="absolute bottom-1/4 right-1/3 w-80 h-80 bg-accent-blue/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        
        {/* 内容 */}
        <div className="relative z-10 container mx-auto px-6 sm:px-8 lg:px-12 pt-32 pb-16">
          <div className="flex flex-col lg:flex-row gap-12 items-start">
            {/* 左侧文字内容 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="flex-1 max-w-2xl"
            >
              <div className="inline-flex items-center gap-2 glass-effect px-4 py-2 rounded-full mb-6">
                <Sparkles className={`w-4 h-4 ${colors.accent}`} />
                <span className="text-sm font-medium text-white/80">100 Apps · One Vision</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-6 text-white leading-tight">
                {category?.title || t.categories[seriesId]}
              </h1>
              
              {'title' in narrative && narrative.title && (
                <p className="text-lg sm:text-xl text-white/70 mb-4">
                  {narrative.title}
                </p>
              )}
              
              {'body' in narrative && (
                <p className="text-white/60 whitespace-pre-line leading-relaxed">
                  {narrative.body}
                </p>
              )}
              
              <div className="flex items-center gap-4 mt-8">
                <div className="glass-effect px-4 py-2 rounded-lg">
                  <span className="text-2xl font-bold text-white">{allProjects.length}</span>
                  <span className="text-white/60 ml-2 text-sm">{t.nav.projects}</span>
                </div>
              </div>
            </motion.div>

            {/* 右侧精选项目卡片 */}
            {featured && (
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="w-full lg:w-[380px]"
              >
                <div className="glass-effect rounded-2xl overflow-hidden">
                  <div className="relative h-48 overflow-hidden">
                    <ImageWithFallback
                      src={featured.image}
                      alt={featured.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                    <div className="absolute bottom-4 left-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${colors.glow} ${colors.accent}`}>
                        {t.common.featured || '精选'}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <h2 className="text-lg font-bold text-white mb-2 line-clamp-2">
                      {featured.title}
                    </h2>
                    {featured.subtitle && (
                      <p className="text-sm text-white/60 mb-2 line-clamp-1">
                        {featured.subtitle}
                      </p>
                    )}
                    <p className="text-sm text-white/50 line-clamp-2">
                      {featured.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </div>
        
        {/* 底部渐变过渡 */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent" />
      </div>

      {/* 100 Apps 阶段概览 */}
      <div className="bg-background py-16">
        <div className="container mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-8"
          >
            <div className="flex items-center gap-3 mb-4">
              <Layers className="w-5 h-5 text-muted-foreground" />
              <h2 className="text-xl sm:text-2xl font-bold">{t.collections.challenge100.title}</h2>
            </div>
          </motion.div>
          
          <div className="grid md:grid-cols-4 gap-4">
            {STAGE_KEYS.map((key: StageKey, index) => {
              const stage = stages[key]
              const isActive = `${key}100` === seriesId
              return (
                <motion.div
                  key={key}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`p-5 rounded-2xl border transition-all duration-300 ${
                    isActive 
                      ? 'bg-card border-primary/30 shadow-lg' 
                      : 'bg-card/50 border-border hover:border-border/80'
                  }`}
                >
                  <p className={`text-xs font-semibold uppercase tracking-wide mb-2 ${
                    isActive ? colors.accent : 'text-muted-foreground'
                  }`}>
                    {t.categories[`${key}100` as HundredSeriesId]}
                  </p>
                  <h3 className="text-sm font-bold mb-2">{stage.title}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {stage.description}
                  </p>
                </motion.div>
              )
            })}
          </div>
        </div>
      </div>

      {/* 项目网格 */}
      <div className="bg-background pb-20">
        <div className="container mx-auto px-6 sm:px-8 lg:px-12">
          <div className="flex items-baseline justify-between mb-8">
            <h2 className="text-xl sm:text-2xl font-bold">
              {category?.title || t.categories[seriesId]}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t.collections.challenge100.metrics.totalLabel}: {allProjects.length}
            </p>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="glass-effect p-6 rounded-2xl">
                <div className="animate-spin w-8 h-8 border-2 border-accent-blue border-t-transparent rounded-full" />
              </div>
            </div>
          ) : allProjects.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {allProjects.map((project, index) => (
                <motion.div
                  key={project.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                >
                  <ProjectCard project={project} />
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="glass-effect inline-block p-8 rounded-2xl">
                <p className="text-muted-foreground">{t.common.noProjects}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 底部 CTA */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-accent-blue/10 via-accent-purple/10 to-accent-emerald/10" />
        <div className="relative container mx-auto px-6 sm:px-8 lg:px-12 py-16">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold mb-3">
                {t.collections.challenge100.cta.title}
              </h2>
              <p className="text-muted-foreground max-w-xl">
                {t.collections.challenge100.cta.subtitle}
              </p>
            </div>
            <div className="flex gap-4">
              <Link
                to={getLocalizedPath('/challenge100')}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-foreground text-background font-medium hover:bg-foreground/90 transition-all"
              >
                {t.collections.challenge100.cta.primary}
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to={getLocalizedPath('/challenge100/journey')}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-border bg-background/50 font-medium hover:bg-background transition-all"
              >
                {t.collections.challenge100.cta.secondary}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  )
}
